(() => {
  const trackProductAction = (name) => {
    if (window.Shopify?.designMode) return;
    try {
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch {}
    try {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        Promise.resolve(window.Shopify.analytics.publish(`miaauraedit:${name}`, {})).catch(() => {});
      }
    } catch {}
  };
  const motionStyle = getComputedStyle(document.documentElement);
  const openResponse = Number(motionStyle.getPropertyValue('--mae-dialog-open-response')) || 16;
  const closeResponse = Number(motionStyle.getPropertyValue('--mae-dialog-close-response')) || 12;
  const init = (root) => {
    root.querySelectorAll('[data-product-section], [data-moonlit-product-section]').forEach((section) => {
      if (section.dataset.summaryToolsInitialized === 'true') return;
      section.dataset.summaryToolsInitialized = 'true';
      const availability = section.querySelector('[data-product-availability]');
      const updateAvailability = (available) => {
        if (!availability || availability.dataset.available === String(available)) return;
        const label = available ? availability.dataset.availableLabel : availability.dataset.soldLabel;
        availability.dataset.available = String(available);
        availability.title = label;
        availability.querySelector('[data-availability-label]').textContent = label;
      };
      section.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.matches('[data-purchase-choice]')) {
          if (target.checked && target.closest('.purchase-option--set')) trackProductAction('mae_select_set');
          return;
        }
        if (!target.matches('.product-select')) return;
        const option = target.selectedOptions[0];
        updateAvailability(option?.value ? option.dataset.available === 'true' : availability?.dataset.productAvailable === 'true');
      });
      section.addEventListener('moonlit:variant-change', (event) => {
        updateAvailability(event.detail ? event.detail.available === true : availability?.dataset.productAvailable === 'true');
      });
      section.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : event.target?.parentElement;
        const link = target?.closest('.product-order-inclusions__item--returns a, .product-order-inclusions__item--warranty a, .product-set-offer');
        if (!link || !section.contains(link)) return;
        if (link.matches('.product-set-offer')) trackProductAction('mae_shop_the_set');
        else if (link.closest('.product-order-inclusions__item--returns')) trackProductAction('mae_returns_view');
        else trackProductAction('mae_warranty_view');
      }, { capture: true, passive: true });

      section.querySelectorAll('[data-fair-pricing-open]').forEach((trigger) => {
        const dialog = document.getElementById(trigger?.getAttribute('aria-controls'));
        if (!trigger || !dialog || typeof dialog.showModal !== 'function') return;
        const panel = dialog.querySelector('[data-fair-pricing-panel]');
        const scrim = dialog.querySelector('[data-fair-pricing-scrim]');
        const copy = dialog.querySelector('[data-fair-pricing-copy]');
        if (!panel || !scrim || !copy) return;
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        const mobile = matchMedia('(max-width: 749px)');
        const price = trigger.dataset.explanation === 'pearls' ? null : section.querySelector('[data-current-price]');
        const priceGroup = section.querySelector('.product-price__current');
        let priceMeasure;
        const alignPriceInfo = () => {
          if (!mobile.matches || !price || !priceGroup) return;
          priceMeasure ||= document.createElement('canvas').getContext('2d');
          if (!priceMeasure) return;
          const style = getComputedStyle(price);
          priceMeasure.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
          const numberDescent = Math.max(0, priceMeasure.measureText('9').actualBoundingBoxDescent || 0);
          const currencySymbol = price.textContent.match(/\p{Sc}/u)?.[0];
          const symbolDescent = currencySymbol ? Math.max(numberDescent, priceMeasure.measureText(currencySymbol).actualBoundingBoxDescent || 0) : numberDescent;
          const value = `${Math.round((numberDescent + symbolDescent) * 500) / 1000}px`;
          if (priceGroup.style.getPropertyValue('--mia-number-symbol-offset') !== value) priceGroup.style.setProperty('--mia-number-symbol-offset', value);
          section.style.setProperty('--mia-number-symbol-offset', value);
        };
        if (price && priceGroup) {
          alignPriceInfo();
          new MutationObserver(alignPriceInfo).observe(price, { childList: true, characterData: true, subtree: true });
          document.fonts?.ready.then(alignPriceInfo);
        }
        let position = 0;
        let velocity = 0;
        let opacity = 0;
        let opacityVelocity = 0;
        let targetPosition = 0;
        let targetOpacity = 1;
        let distance = 48;
        let frame = 0;
        let lastTime = 0;
        let state = 'closed';
        let drag;
        let suppressClickUntil = 0;
        const render = () => {
          panel.style.transform = `translate3d(0, ${position}px, 0)`;
          panel.style.opacity = String(Math.max(0, Math.min(1, opacity)));
          scrim.style.opacity = panel.style.opacity;
        };
        const stop = () => {
          cancelAnimationFrame(frame);
          frame = 0;
        };
        const releasePointer = () => {
          const previousDrag = drag;
          drag = undefined;
          delete panel.dataset.dragging;
          if (previousDrag && panel.hasPointerCapture(previousDrag.id)) panel.releasePointerCapture(previousDrag.id);
          return previousDrag;
        };
        const measure = () => {
          if (!dialog.open) return;
          distance = Math.max(48, panel.getBoundingClientRect().height + 32);
          panel.dataset.scrollable = String(copy.scrollHeight > copy.clientHeight + 1);
          if (state === 'closing') targetPosition = mobile.matches ? distance : 8;
        };
        const finish = () => {
          stop();
          position = targetPosition;
          opacity = targetOpacity;
          velocity = 0;
          opacityVelocity = 0;
          render();
          panel.style.willChange = 'auto';
          scrim.style.willChange = 'auto';
          if (state === 'closing') dialog.close();
          else state = 'open';
        };
        const tick = (time) => {
          const elapsed = Math.min((time - lastTime) / 1000 || 1 / 60, 1 / 30);
          lastTime = time;
          const response = state === 'closing' ? closeResponse : openResponse;
          let remaining = elapsed;
          while (remaining > 0) {
            const step = Math.min(remaining, 1 / 240);
            velocity += (-response * response * (position - targetPosition) - 2 * response * velocity) * step;
            position += velocity * step;
            opacityVelocity += (-response * response * (opacity - targetOpacity) - 2 * response * opacityVelocity) * step;
            opacity += opacityVelocity * step;
            remaining -= step;
          }
          render();
          if (Math.abs(position - targetPosition) < .25 && Math.abs(velocity) < 2 && Math.abs(opacity - targetOpacity) < .005) {
            finish();
            return;
          }
          frame = requestAnimationFrame(tick);
        };
        const move = (nextPosition, nextOpacity, nextState, initialVelocity = velocity) => {
          stop();
          targetPosition = nextPosition;
          targetOpacity = nextOpacity;
          state = nextState;
          velocity = initialVelocity;
          if (reducedMotion.matches) {
            finish();
            return;
          }
          panel.style.willChange = 'transform, opacity';
          scrim.style.willChange = 'opacity';
          lastTime = performance.now();
          frame = requestAnimationFrame(tick);
        };
        const cleanUp = () => {
          stop();
          releasePointer();
          state = 'closed';
          panel.style.willChange = 'auto';
          scrim.style.willChange = 'auto';
          if (!document.querySelector('[data-fair-pricing][open]')) document.documentElement.classList.remove('fair-pricing-open');
          trigger.setAttribute('aria-expanded', 'false');
          if (trigger.isConnected) trigger.focus({ preventScroll: true });
        };
        const close = (initialVelocity = velocity) => {
          if (!dialog.open) return;
          releasePointer();
          measure();
          move(mobile.matches ? distance : 8, 0, 'closing', initialVelocity);
        };
        trigger.addEventListener('click', () => {
          if (!dialog.open) {
            position = reducedMotion.matches ? 0 : mobile.matches ? 24 : 8;
            opacity = 0;
            velocity = 0;
            opacityVelocity = 0;
            copy.scrollTop = 0;
            render();
            dialog.showModal();
            trackProductAction(trigger.dataset.explanation === 'pearls' ? 'mae_premium_pearls_open' : 'mae_fair_pricing_open');
          }
          measure();
          document.documentElement.classList.add('fair-pricing-open');
          trigger.setAttribute('aria-expanded', 'true');
          move(0, 1, 'opening');
        });
        dialog.querySelectorAll('[data-fair-pricing-close]').forEach((button) => button.addEventListener('click', () => close()));
        scrim.addEventListener('click', () => close());
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          close();
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) close();
        });
        dialog.addEventListener('close', cleanUp);
        panel.addEventListener('pointerdown', (event) => {
          suppressClickUntil = 0;
          if (!mobile.matches || !dialog.open || event.isPrimary === false || event.button !== 0) return;
          if (event.target.closest('button, a, input, select, textarea') && !event.target.closest('[data-fair-pricing-handle]')) return;
          if (event.target.closest('[data-fair-pricing-copy]') && panel.dataset.scrollable === 'true') return;
          stop();
          measure();
          state = 'dragging';
          drag = { id: event.pointerId, startY: event.clientY, startPosition: position, startOpacity: opacity, moved: false, samples: [{ y: event.clientY, time: event.timeStamp }] };
          panel.dataset.dragging = 'true';
          panel.style.willChange = 'transform, opacity';
          panel.setPointerCapture(event.pointerId);
        });
        panel.addEventListener('pointermove', (event) => {
          if (!drag || event.pointerId !== drag.id) return;
          const delta = event.clientY - drag.startY;
          const nextPosition = drag.startPosition + delta;
          position = nextPosition < 0 ? nextPosition * .15 : nextPosition;
          opacity = Math.max(0, Math.min(1, drag.startOpacity + (drag.startPosition - position) / distance));
          if (Math.abs(delta) > 6) drag.moved = true;
          drag.samples.push({ y: event.clientY, time: event.timeStamp });
          while (drag.samples.length > 2 && event.timeStamp - drag.samples[0].time > 100) drag.samples.shift();
          if (event.cancelable) event.preventDefault();
          render();
        });
        const endDrag = (event, cancelled = false) => {
          if (!drag || event.pointerId !== drag.id) return;
          const firstSample = drag.samples[0];
          const elapsed = Math.max(1, event.timeStamp - firstSample.time);
          const releaseVelocity = cancelled ? 0 : Math.max(-1600, Math.min(1600, (event.clientY - firstSample.y) * 1000 / elapsed));
          const previousDrag = releasePointer();
          if (previousDrag.moved) suppressClickUntil = performance.now() + 350;
          if (!cancelled && previousDrag.moved && position > 12 && position + releaseVelocity * .15 > distance * .28) close(releaseVelocity);
          else move(0, 1, 'opening', releaseVelocity);
        };
        panel.addEventListener('pointerup', (event) => endDrag(event));
        panel.addEventListener('pointercancel', (event) => endDrag(event, true));
        panel.addEventListener('lostpointercapture', (event) => {
          if (drag?.id === event.pointerId) {
            releasePointer();
            move(0, 1, 'opening', 0);
          }
        });
        panel.addEventListener('click', (event) => {
          if (event.detail === 0 || performance.now() >= suppressClickUntil) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);
        const motionChange = () => {
          if (reducedMotion.matches && frame) finish();
        };
        const mobileChange = () => {
          if (!section.isConnected) return;
          alignPriceInfo();
          measure();
          if (!mobile.matches && drag) {
            releasePointer();
            move(0, 1, 'opening', 0);
          }
        };
        reducedMotion.addEventListener('change', motionChange);
        mobile.addEventListener('change', mobileChange);
        const observer = new ResizeObserver(measure);
        observer.observe(panel);
        const resize = () => { if (section.isConnected) { alignPriceInfo(); measure(); } };
        window.addEventListener('resize', resize, { passive: true });
        const dispose = section.miaDispose;
        section.miaDispose = () => { dispose?.(); stop(); observer.disconnect(); window.removeEventListener('resize', resize); reducedMotion.removeEventListener('change', motionChange); mobile.removeEventListener('change', mobileChange); };
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('mia:page:mount', (event) => init(event.detail.root));
})();
